"use client";

import { useState } from "react";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { parseYouTubeUrl, buildEmbedUrl } from "@/utils/youtube";

type WhaleWatchVideoModalProps = {
  videoUrl: string;
  label?: string;
  className?: string;
};

export function WhaleWatchVideoModalLink({
  videoUrl,
  label = "See Whale Watch in action",
  className,
}: WhaleWatchVideoModalProps) {
  const [open, setOpen] = useState(false);
  const { youtubeId, startSeconds } = parseYouTubeUrl(videoUrl);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 text-success hover:text-success/80 transition-colors",
          className
        )}
      >
        {label}
        <ArrowRight className="size-3.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-4xl w-[95vw] p-0"
          contentClassName="p-0 gap-0 overflow-hidden bg-[#0b0f14]"
        >
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-white/10 bg-gradient-to-r from-white/10 to-transparent">
            <DialogTitle className="text-base font-semibold text-foreground">
              Whale Watch
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              See where the sharp money moves.
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 pt-5">
            {open && youtubeId && (
              <div className="relative aspect-video rounded-xl overflow-hidden border border-white/10 bg-black/60 shadow-glow-primary-strong">
                <iframe
                  src={buildEmbedUrl(youtubeId, startSeconds)}
                  title="Whale Watch"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            )}

            {!youtubeId && (
              <div className="aspect-video rounded-xl bg-black/40 border border-white/10 flex items-center justify-center">
                <span className="text-muted-foreground text-sm">Video unavailable</span>
              </div>
            )}

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ExternalLink className="size-3.5" />
                Watch on YouTube
              </a>
              <span className="text-xs text-muted-foreground/70">Whale Watch</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
